import { memo, useCallback, useMemo, MouseEvent } from 'react';
import ComboBox, { ComboBoxItem } from '@raydiant/raydial/components/ComboBox';
import { LocationACL, ResourceGrant } from '@raydiant/api-client-js';
import cn from 'classnames';
import {
  aclToSelectItem,
  deviceACLToSelectItem,
  GRANT_SELECT_OPTIONS,
} from '../../LibraryPage/v2/components/ShareDialog/ShareDialog';

export interface GrantOption {
  id: string;
  title: string;
  description: string;
  disabled: boolean;
}

export const DEVICE_GRANT_SELECT_OPTIONS: GrantOption[] = [
  {
    id: 'read',
    title: 'Viewer',
    description: 'View the device',
    disabled: false,
  },
  {
    id: 'update',
    title: 'Editor',
    description: 'View and edit the device',
    disabled: false,
  },
  {
    id: 'publish',
    title: 'Publisher',
    description: 'Edit, assign playlists, and publish device',
    disabled: false,
  },
  {
    id: 'share',
    title: 'Access Manager',
    description: 'Publisher rights plus share device',
    disabled: false,
  },
  {
    id: 'delete',
    title: 'Device Admin',
    description: 'All permissions, including delete device',
    disabled: false,
  },
];

interface DeviceGrantSelectProps {
  className?: string;
  contentType: string;
  grantOptions?: GrantOption[];
  acl: LocationACL;
  isDisabled?: boolean;
  isOwner?: boolean;
  onGrantChange: (acl: LocationACL, grant: ResourceGrant) => void;
  onRemoveAccess: (e: MouseEvent<HTMLDivElement>) => void;
}

const DeviceGrantSelect = memo(
  ({
    className,
    grantOptions = GRANT_SELECT_OPTIONS,
    contentType,
    acl,
    isDisabled = false,
    isOwner = false,
    onGrantChange,
    onRemoveAccess,
  }: DeviceGrantSelectProps) => {
    const handleSelectionChange = useCallback(
      (grant: string) => {
        if (
          ['read', 'update', 'share', 'delete', 'full', 'publish'].includes(
            grant,
          )
        ) {
          onGrantChange(acl, grant as ResourceGrant);
        }
      },
      [acl, onGrantChange],
    );

    const handleRemoveAccess = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onRemoveAccess(e);
      },
      [onRemoveAccess],
    );

    const grantOptionsWithFullPermission = useMemo(() => {
      return grantOptions.map((option) => (
        <ComboBoxItem
          key={option.id}
          id={option.id}
          textValue={option.title}
          isDisabled={option.disabled}
          checkMarkSelected
        >
          <div className="cursor-pointer">
            <div>{option.title}</div>
            <div className="text-sm text-gray-600 h-3.5">
              {option.description}
            </div>
          </div>
        </ComboBoxItem>
      ));
    }, [grantOptions]);

    const selectedKey =
      contentType === 'device'
        ? deviceACLToSelectItem(acl)
        : aclToSelectItem(acl);

    return (
      <ComboBox
        isReadOnly
        onSelectionChange={(e) => handleSelectionChange(e as ResourceGrant)}
        selectedKey={isOwner ? 'delete' : selectedKey}
        isDisabled={isDisabled}
        className={cn(
          'w-full md:w-[278px] [&_button]:disabled:border-gray-200 [&_input]:disabled:text-gray-600 [&_input]:disabled:opacity-100 [&_input_div]:disabled:opacity-100',
          className,
        )}
        aria-label="Select access level"
      >
        {grantOptionsWithFullPermission}
        <ComboBoxItem id="remove-access">
          <div
            className="text-red-700 cursor-pointer"
            onClick={handleRemoveAccess}
          >
            Remove Access
          </div>
        </ComboBoxItem>
      </ComboBox>
    );
  },
);

export default DeviceGrantSelect;
